import { TBilling, TPayment } from "../../Models/Billing";
import { TInboundFields } from "../../Models/Inbound";
import { TStore } from "../../hooks/useService";
import {
  ORDER_CATEGORY,
  TRANSACTION_STATUS,
  TRANSACTION_TYPES,
} from "../../utils/constant";
import { POST, respond } from "../Helpers";
import {
  TCompletePhysicalOrderData,
  TCompletePhysicalOrderService,
  TCreateDeliveryData,
  TCreateDeliveryService,
  TCreateOrUpdateLineDetailsData,
  TCreateOrUpdateLineDetailsService,
  TCreateOrUpdateMeltingLotData,
  TCreateOrUpdateMeltingLotService,
  TCreatePhysicalOrderData,
  TCreatePhysicalOrderService,
  TGetDeliverData,
  TGetDeliveryService,
  TExtractXRFReportData,
  TExtractXRFReportService,
  TGetGoldCategoriesData,
  TGetGoldCategoriesService,
  TGetLineDetailsData,
  TGetLineDetailsService,
  TGetLotDetailsData,
  TGetLotDetailsService,
  TGetPhysicalOrderData,
  TGetPhysicalOrderService,
  TGetProductsData,
  TGetProductsService,
  TGetTransactionData,
  TGetTransactionService,
  TGetTransactionsCountData,
  TGetTransactionsCountService,
  TGetTransactionsData,
  TGetTransactionsService,
  TInitiateTransactionData,
  TInitiateTransactionService,
  TPaymentFileUpload,
  TRecordBillingPayment,
  TRecordBillingPaymentData,
  TSearchPvcCentersData,
  TSearchPvcCentersService,
  TSettleInboundData,
  TSettleInboundService,
  TUpdateDeliveryStatusData,
  TUpdateDeliveryStatusService,
  TUpdateTransactionData,
  TUpdateTransactionService,
  TUploadData,
  TGetTransitionHistory,
  TFetchCharges,
  TFetchChargesData,
  TLotDetailsCount,
  TLotDetailsCountData,
  TConfirmPhysicalOrderService,
  TConfirmPhysicalOrderData,
  TFetchJobworkInventoryService,
  TFetchJobworkInventoryData,
  TGetPvcCentersService,
  TGetPvcCentersData,
  TDownloadSlip,
  TDownloadSlipData,
  TCancelTransactionService,
  TCancelTransactionData,
  TFetchTransactionDocumentsService,
  TFetchTransactionDocumentsData,
  TDeleteBillingPaymentService,
  TDeleteBillingPaymentData,
  TConfirmBillingPaymentService,
  TConfirmBillingPaymentData,
  TFetchConcentDocumentsService,
  TFetchConsentData,
  TFetchAnnexureDocumentsService,
  TGetAllProductsService,
  TGetAllProductsData,
  TFetchTransactionDocumentsListService,
  TFetchTransactionDocumentsListData,
  TCreateTransactionImage,
  TGetTransactionImages,
  TGetTransactionImagesData,
  TCreateTransactionImageData,
  TDeleteTransactionImage,
  TDeleteTransactionImageData,
  TdeleteLotDetailsService,
  TdeleteLotDetailsData,
  TCreateProductScanException,
  TCreateProductScanExceptionData,
  TGetEcomRedeemAtPvcOrdersService,
  TGetEcomRedeemAtPvcOrderDetailService,
  TFetchXRFReportService,
  TFetchXRFData,
  TGetTransactionInvoices,
  TGetTransactionInvoicesData,
  TCreateTransactionInvoice,
  TCreateTransactionInvoiceData,
  TDeleteTransactionInvoice,
  TDeleteTransactionInvoiceData
} from "./types";

export const getGoldCategoriesOptions: TGetGoldCategoriesService = ({
  type,
}) => {
  return async (store: TStore) => {
    const { error, data } = await POST<TGetGoldCategoriesData>(
      "/getDropdownOptions",
      { type },
      true
    );

    return respond(data, error);
  };
};

export const initiateTransaction: TInitiateTransactionService = ({
  currency,
  customerId,
  pvcCenterId,
  transactionType,
  weightUOM,
}) => {
  return async (store: TStore) => {
    const { setInboundDetails, setPvcCenterDetails, setTransactionInfo } =
      store.transactionsStore;
    const { error, data } = await POST<TInitiateTransactionData>(
      "/initiateTransaction?type=" + transactionType,
      { currency, customerId, pvcCenterId, transactionType, weightUOM },
      true
    );
    if (data !== null) {
      setInboundDetails((prev) => ({ ...prev, id: data.inboundId }));
      setPvcCenterDetails(data.pvcCenter);
      setTransactionInfo((prev) => ({ ...prev, ...data.transaction }));
    }
    return respond(data, error);
  };
};

export const updateTransaction: TUpdateTransactionService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { error, data } = await POST<TUpdateTransactionData>(
      "/updateTransaction?type=" + transactionType,
      { ...params },
      true
    );
    if (data !== null)
      store.transactionsStore.setInboundDetails((prev) => ({
        ...prev,
        ...data,
      }));
    if (params.inbound.inboundHeader.secondaryTransactionLocation) {
      store.transactionsStore.setTransactionInfo((prev) => ({
        ...prev,
        secondaryTransactionLocationId:
          params.inbound.inboundHeader.secondaryTransactionLocation,
      }));
    }
    return respond(data, error);
  };
};

export const getTransaction: TGetTransactionService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const {
      setInboundDetails,
      setTransactionInfo,
      setPhysicalOrder,
      setInboundOrder,
      setLineItems,
      setBillingData,
      setPayments,
      setPvcCenterDetails,
      setIsInventory
    } = store.transactionsStore;
    const { setCustomerConfig } = store.customerStore;
    const { error, data } = await POST<TGetTransactionData>(
      "/getTransaction?type=" + transactionType,
      { ...params },
      true
    );
    if (data !== null) {
      const { inbound, order, pvcCenter, customer, inventoryCount, ...transactionBasic } = data;
      const { secondaryTransactionLocation, ...inboundDetails } = inbound;
      setInboundDetails(inboundDetails as TInboundFields);
      setTransactionInfo(transactionBasic);
      setCustomerConfig(customer);
      setPvcCenterDetails(pvcCenter);
      setIsInventory(inventoryCount)
      if (order.length)
        order.forEach((ord) => {
          switch (transactionBasic.transactionType) {
            case TRANSACTION_TYPES.BUY_BACK:
              if (ord.orderCategory === ORDER_CATEGORY.SELL) {
                const { billing } = ord;
                const { payments } = billing!;
                setPhysicalOrder(ord);
                setBillingData(billing!);
                setPayments(payments!);
              }
              break;
            case TRANSACTION_TYPES.GMS:
              if (ord.orderCategory === ORDER_CATEGORY.GMS) {
                const { billing } = ord;
                const { payments } = billing!;
                setPhysicalOrder(ord);
                setBillingData(billing!);
                setPayments(payments!);
              }
              break;

            case TRANSACTION_TYPES.PURITY_TESTING:
            case TRANSACTION_TYPES.JOB_WORK:
              if (ord.orderCategory === ORDER_CATEGORY.SERVICE) {
                const { billing } = ord;
                const { payments } = billing!;
                setPhysicalOrder(ord);
                setBillingData(billing!);
                setPayments(payments!);
              }
              break;

            default:
              if (ord.orderCategory === ORDER_CATEGORY.BUY) {
                const { lineItems, billing } = ord;
                const { payments } = billing!;
                setPhysicalOrder(ord);
                setLineItems(lineItems!);
                setBillingData(billing!);
                setPayments(payments!);
              } else if (ord.orderCategory === "SELL") setInboundOrder(ord);
              break;
          }
        });
    }
    return respond(data, error);
  };
};

export const getTransactions: TGetTransactionsService = (params) => {
  return async (store: TStore) => {
    const { setTransactions } = store.transactionsStore;
    const { error, data } = await POST<TGetTransactionsData>(
      "/getTransactions",
      { ...params },
      true
    );

    if (data !== null) setTransactions(data!);
    return respond(data, error);
  };
};
export const getEcomRedeemAtPvcOrders: TGetEcomRedeemAtPvcOrdersService = (params) => {
  return async (store: TStore) => {
    const { setTransactions } = store.ecomTransactionStore;
    const { error, data } = await POST<TGetTransactionsData>(
      "/getEcomRedeemAtPvcOrders",
      { ...params },
      true
    );

    if (data !== null) setTransactions(data!);
    return respond(data, error);
  };
};

export const completeEcomRedeemAtPvcOrder: any = (params : any) => {
  return async () => {
    const { error, data } = await POST<any>(
      "/completeEcomRedeemAtPvcOrder",
      { ...params },
      true
    );

    if (data !== null)
    return respond(data, error);
    else
    return respond(null, error);
  };
};
export const getEcomRedeemAtPvcOrderDetails: TGetEcomRedeemAtPvcOrderDetailService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const {
      setTransactionInfo,
      setecomphysicalOrder,
      setLineItems,
      setBillingData,
      setPayments,
      setPvcCenterDetails,
      setIsInventory
    } = store.ecomTransactionStore;
    const { setCustomerConfig } = store.customerStore;
    const { error, data } = await POST<TGetTransactionData>(
      "/getTransaction?type=" + transactionType,
      { ...params },
      true
    );
    if (data !== null) {
      const { inbound, order, pvcCenter, customer, inventoryCount, ...transactionBasic } = data;
      const { secondaryTransactionLocation, ...inboundDetails } = inbound;
      setTransactionInfo(transactionBasic);
      setCustomerConfig(customer);
      setPvcCenterDetails(pvcCenter);
      setIsInventory(inventoryCount);
      // setecomphysicalOrder(order);

      if (Array.isArray(order) && order.length > 0)
        order.forEach((ord) => {
          switch (transactionBasic.transactionType) {
            case TRANSACTION_TYPES.BUY_BACK:
              if (ord.orderCategory === ORDER_CATEGORY.SELL) {
                const { billing } = ord;
                const { payments } = billing!;
                setecomphysicalOrder(ord);
                setBillingData(billing!);
                setPayments(payments!);
              }
              break;
            case TRANSACTION_TYPES.GMS:
              if (ord.orderCategory === ORDER_CATEGORY.GMS) {
                const { billing } = ord;
                const { payments } = billing!;
                setecomphysicalOrder(ord);
                setBillingData(billing!);
                setPayments(payments!);
              }
              break;

            case TRANSACTION_TYPES.PURITY_TESTING:
            case TRANSACTION_TYPES.JOB_WORK:
              if (ord.orderCategory === ORDER_CATEGORY.SERVICE) {
                const { billing } = ord;
                const { payments } = billing!;
                setecomphysicalOrder(ord);
                setBillingData(billing!);
                setPayments(payments!);
              }
              break;

            default:
              if (ord.orderCategory === "SELL") {
                const { lineItems, billing } = ord;
                const { payments } = billing!;
                setecomphysicalOrder(ord);
                setLineItems(lineItems!);
                setBillingData(billing!);
                setPayments(payments!);
              } 
              break;
          }
        });
    }
    return respond(data, error);
  };
};

export const getTransactionsCount: TGetTransactionsCountService = (params) => {
  return async (store: TStore) => {
    const { error, data } = await POST<TGetTransactionsCountData>(
      "/getTransactionsCount",
      { ...params },
      true
    );
    return respond(data, error);
  };
};

export const searchPvcCenters: TSearchPvcCentersService = (params) => {
  return async (store: TStore) => {
    const { error, data } = await POST<TSearchPvcCentersData>(
      "/searchPvcCenters",
      { ...params },
      true
    );

    return respond(data, error);
  };
};

export const getPvcCenters: TGetPvcCentersService = (params) => {
  return async (store: TStore) => {
    const { setPvcCenters } = store.transactionsStore;
    const { error, data } = await POST<TGetPvcCentersData>(
      "/getPvcCenters",
      { ...params },
      true
    );
    if (data != null) setPvcCenters(data);
    return respond(data, error);
  };
};

export const createOrUpdateLineDetails: TCreateOrUpdateLineDetailsService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { error, data } = await POST<TCreateOrUpdateLineDetailsData>(
      "/createOrUpdateLineDetails?type=" + transactionType,
      { ...params },
      true
    );

    return respond(data, error);
  };
};

export const convertToPurityTesting: TdeleteLotDetailsService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { error, data } = await POST<TdeleteLotDetailsData>(
      "/convertLotsToPurityTesting?type=" + transactionType,
      { ...params },
      true
    );

    return respond(data, error);
  };
};

export const createOrUpdateLotDetails: TCreateOrUpdateMeltingLotService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { error, data } = await POST<TCreateOrUpdateMeltingLotData>(
      "/createOrUpdateLotDetails?type=" + transactionType,
      { ...params },
      true
    );

    return respond(data, error);
  };
};

export const getLineDetails: TGetLineDetailsService = (params) => {
  return async (store: TStore) => {
    const { setLineDetails: setLineItems } = store.transactionsStore;
    const { error, data } = await POST<TGetLineDetailsData>(
      "/getLineDetails",
      { ...params },
      true
    );
    if (data != null) setLineItems(data);
    return respond(data, error);
  };
};

export const getLotDetails: TGetLotDetailsService = (params) => {
  return async (store: TStore) => {
    const { setMeltingLot } = store.transactionsStore;
    const { error, data } = await POST<TGetLotDetailsData>(
      "/getLotDetails",
      { ...params },
      true
    );
    if (data != null) setMeltingLot(data);
    return respond(data, error);
  };
};

export const settleInbound: TSettleInboundService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const {
      setInboundOrder,
      setBillingData,
      setPayments,
      setTransactionInfo,
      setPhysicalOrder,
    } = store.transactionsStore;
    const { error, data } = await POST<TSettleInboundData>(
      "/settleInbound?type=" + transactionType,
      { ...params },
      true
    );
    if (data != null) {
      const { order, transactionStatus } = data;
      setTransactionInfo((prev) => ({ ...prev, status: transactionStatus }));
      if (transactionType != TRANSACTION_TYPES.JOB_WORK) {
        const { billing } = order;
        const { payments } = billing!;
        setInboundOrder(order);
        setBillingData(billing!);
        payments && setPayments(payments);
      }
      if (transactionType === TRANSACTION_TYPES.BUY_BACK || transactionType === TRANSACTION_TYPES.PURITY_TESTING || transactionType === TRANSACTION_TYPES.GMS)
        setPhysicalOrder(order);
    }
    return respond(data, error);
  };
};

export const getProducts: TGetProductsService = (params) => {
  return async (store: TStore) => {
    const { setProducts } = store.transactionsStore;
    const { user } = store.authStore;
    const { error, data } = await POST<TGetProductsData>(
      "/getProducts",
      params,
      true
    );
    if (data !== null) setProducts(data.data.skus);
    return respond(data, error);
  };
};

export const getecomRedeemAtPvcProducts: TGetProductsService = (params) => {
  return async (store: TStore) => {
    const { setProducts } = store.ecomTransactionStore;
    const { user } = store.authStore;
    const { error, data } = await POST<TGetProductsData>(
      "/getProducts",
      params,
      true
    );
    if (data !== null) setProducts(data.data.skus);
    return respond(data, error);
  };
};

export const getAllProducts: TGetAllProductsService = (params) => {
  return async (store: TStore) => {
    const { setProducts } = store.transactionsStore;
    const { user } = store.authStore;
    const { error, data } = await POST<TGetAllProductsData>(
      "/getAllProducts",
      params,
      true
    );
    if (data !== null) setProducts(data.data.skus);
    return respond(data, error);
  };
};

export const createPhysicalOrder: TCreatePhysicalOrderService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { setInboundOrder, setBillingData, setPayments, setLineItems } =
      store.transactionsStore;
    const { error, data } = await POST<TCreatePhysicalOrderData>(
      "/createPhysicalOrder?type=" + transactionType,
      params,
      true
    );
    if (data != null) {
      const { billing, lineItems } = data;
      const { payments } = billing!;
      setInboundOrder(data);
      setBillingData(billing!);
      payments && setPayments(payments);
      setLineItems(lineItems!);
    }
    return respond(data, error);
  };
};

export const getPhysicalOrder: TGetPhysicalOrderService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { setPhysicalOrder, setLineItems, setBillingData, setPayments } =
      store.transactionsStore;
    const { error, data } = await POST<TGetPhysicalOrderData>(
      "/getPhysicalOrder?type=" + transactionType,
      params,
      true
    );
    if (data != null) {
      const { lineItems, billing } = data;
      const { payments } = billing!;
      setPhysicalOrder(data);
      setLineItems(lineItems!);
      setBillingData(billing!);
      payments && setPayments(payments);
    }
    return respond(data, error);
  };
};

export const completePhysicalOrder: TCompletePhysicalOrderService = (
  params
) => {
  return async (store: TStore) => {
    const { transactionType } = params;
    const {
      setPhysicalOrder,
      setLineItems,
      setBillingData,
      setPayments,
      setInboundOrder,
      setTransactionInfo,
    } = store.transactionsStore;
    const { error, data } = await POST<TCompletePhysicalOrderData>(
      "/completePhysicalOrder?type=" + transactionType,
      params,
      true
    );
    if (data !== null && transactionType === TRANSACTION_TYPES.MINTED_SALES) {
      const { physicalOrder, transactionStatus } = data;
      const { billing, lineItems } = physicalOrder;
      const { payments } = billing!;
      setPhysicalOrder(physicalOrder);
      setPayments(payments!);
      setLineItems(lineItems!);
      setBillingData(billing!);
      setTransactionInfo((prev) => ({ ...prev, status: transactionStatus! }));
    } else if (data !== null) {
      const { inboundOrder, physicalOrder, transactionStatus } = data;
      const { lineItems, billing } = physicalOrder;
      const { payments } = billing!;

      setPhysicalOrder(physicalOrder);
      setLineItems(lineItems!);
      setBillingData(billing!);
      setPayments(payments!);
      setInboundOrder((prev) => ({ ...prev, ...inboundOrder }));
      setTransactionInfo((prev) => ({ ...prev, status: transactionStatus! }));
    }
    return respond(data, error);
  };
};

export const confirmPhysicalOrder: TConfirmPhysicalOrderService = (params) => {
  return async (store: TStore) => {
    const { transactionType } = params;
    const { setPhysicalOrder, physicalOrder, setTransactionInfo } = store.transactionsStore
    const { error, data } = await POST<TConfirmPhysicalOrderData>(
      "/confirmPhysicalOrder?type=" + transactionType,
      params,
      true
    );
    if (data !== null) {
      const { updatedLineItems } = data;
      const { lineItems } = physicalOrder
      lineItems?.forEach((lineItem) => {
        updatedLineItems.map(item => {
          if (item.id === lineItem.id) {
            lineItem.serialNumbers = item.serialNumbers
          }
        })
      })
      setPhysicalOrder(prev => ({ ...prev, lineItems }))
      setTransactionInfo(prev => ({ ...prev, status: TRANSACTION_STATUS.PAYMENT_INITIATED }))
    }
    return respond(data, error);
  };
};

export const uploadFile: TPaymentFileUpload = (params) => {
  return async (store: TStore) => {
    const { transactionType } = params;
    const { error, data } = await POST<TUploadData>(
      "/uploadPaymentFile?type=" + transactionType,
      params,
      true
    );
    return respond(data, error);
  };
};

export const recordBillingPayment: TRecordBillingPayment = (params) => {
  return async (store: TStore) => {
    const { transactionType } = params;
    const { setPayments, setBillingData, setTransactionInfo, transactionInfo } =
      store.transactionsStore;
    const { setCustomerConfig } = store.customerStore
    const { error, data } = await POST<TRecordBillingPaymentData>(
      "/recordBillingPayment?type=" + transactionType,
      params,
      true
    );
    if (data !== null) {
      if (data.updatedWalletBalance) setCustomerConfig(prev => ({ ...prev, walletBalance: data.updatedWalletBalance }))
      setBillingData(data.billing as TBilling);
      const paymentObject = data.payment;
      setPayments((prev) => [...prev, paymentObject] as TPayment[]);
      // if (data.order.status) {
      //   let nextStep
      //   switch(transactionType){
      //     case TRANSACTION_TYPES.BUY_SELL:
      //     case TRANSACTION_TYPES.JOB_WORK:
      //       nextStep = TRANSACTION_STATUS.DELIVERY_INITIATED;
      //       break;
      //     default :
      //       nextStep = TRANSACTION_STATUS.COMPLETED;
      //   }
      //   setTransactionInfo({
      //     ...transactionInfo,
      //     status: nextStep,
      //   });
      // }
    }
    return respond(data, error);
  };
};

export const createDelivery: TCreateDeliveryService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { error, data } = await POST<TCreateDeliveryData>(
      "/createDelivery?type=" + transactionType,
      params,
      true
    );
    return respond(data, error);
  };
};

export const updateDeliveryStatus: TUpdateDeliveryStatusService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { error, data } = await POST<TUpdateDeliveryStatusData>(
      "/updateDeliveryStatus?type=" + transactionType,
      params,
      true
    );
    return respond(data, error);
  };
};

export const getDelivery: TGetDeliveryService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { setDeliveryData } = store.transactionsStore;
    const { error, data } = await POST<TGetDeliverData>(
      "/getDelivery?type=" + transactionType,
      params,
      true
    );
    if (data != null) {
      setDeliveryData(data);
    }
    return respond(data, error);
  };
};

export const extractXRFReport: TExtractXRFReportService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { setMeltingLot } = store.transactionsStore;
    const { error, data } = await POST<TExtractXRFReportData>(
      "/extractXRFReport?type=" + transactionType,
      params,
      true
    );

    if (data !== null) {
      setMeltingLot((prev) => {
        return prev.map((lot) => {
          return lot.id === params.lotNumber
            ? { ...lot, fineness: data.fineness, xrfReport: data.xrfReport }
            : lot;
        });
      });
    }
    return respond(data, error);
  };
};

export const getTransitionHistory: TGetTransitionHistory = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { setTransitionHistory } = store.transactionsStore;
    const { data, error } = await POST<any>(
      "/getTransitionHistory?type=" + transactionType,
      params,
      true
    );
    if (data !== null) {
      setTransitionHistory(data);
    }
    // Manage your transaction history here and apply logic here
    return respond(data, error);
  };
};

export const fetchCharges: TFetchCharges = ({
  transactionType,
  globalTransactionNumber,
}) => {
  return async (store: TStore) => {
    const { setCharges } = store.transactionsStore;
    const { data, error } = await POST<TFetchChargesData>(
      "/fetchCharges?type=" + transactionType,
      { globalTransactionNumber },
      true
    );
    if (data !== null) {
      setCharges(data);
    }
    return respond(data, error);
  };
};

export const getLotDetailsCount: TLotDetailsCount = ({
  transactionType,
  globalTransactionNumber,
}) => {
  return async (store: TStore) => {
    const { setTransactionInfo, transactionInfo } = store.transactionsStore;
    const { data, error } = await POST<TLotDetailsCountData>(
      "/getLotDetailsCount?type=" + transactionType,
      { globalTransactionNumber },
      true
    );
    if (data !== null) {
      setTransactionInfo({ ...transactionInfo, lotCounts: data.count });
    }
    return respond(data, error);
  };
};

export const fetchJobworkInventory: TFetchJobworkInventoryService = ({
  globalTransactionNumber,
}) => {
  return async (store: TStore) => {
    const { setJobworkInventory } = store.transactionsStore;
    const { data, error } = await POST<TFetchJobworkInventoryData>(
      "/fetchJobworkInventory",
      { globalTransactionNumber },
      true
    );
    if (data !== null) {
      setJobworkInventory(data);
    }
    return respond(data, error);
  };
};
export const downloadSlip: TDownloadSlip = ({ lotId, transactionType, slipType, globalTransactionNumber }) => {
  return async (store: TStore) => {
    const { data, error } = await POST<TDownloadSlipData>(
      '/downloadSlip?type=' + transactionType,
      { lotId, slipType, globalTransactionNumber },
      true
    )
    return respond(data, error)
  }
}

export const createAndCompleteMintedPhysicalOrder: TCompletePhysicalOrderService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { setBillingData, setPayments, setLineItems, setPhysicalOrder, setTransactionInfo } =
      store.transactionsStore;
    const { error, data } = await POST<TCompletePhysicalOrderData>(
      "/createAndCompleteMintedPhysicalOrder?type=" + transactionType,
      params,
      true
    );
    if (data != null) {
      const { physicalOrder, transactionStatus } = data;
      const { billing, lineItems } = physicalOrder;
      const { payments } = billing!;
      setPhysicalOrder(physicalOrder);
      setPayments(payments!);
      setLineItems(lineItems!);
      setBillingData(billing!);
      setTransactionInfo((prev) => ({ ...prev, status: transactionStatus! }));
    }
    return respond(data, error);
  };
};
export const bulkMintedOrders: any = ({
  transactionType,
  ...params
}: { transactionType: string;[key: string]: any }) => {
  return async (store: any) => {

    const { error, data } = await POST(
      "/bulkMintedOrders?type=" + transactionType,
      params,
      true
    );

    return respond(data, error);
  };
};

export const updateMintedPhysicalOrder: TCompletePhysicalOrderService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { setBillingData, setPayments, setLineItems, setPhysicalOrder, setTransactionInfo } =
      store.transactionsStore;
    const { error, data } = await POST<TCompletePhysicalOrderData>(
      "/updateMintedPhysicalOrder?type=" + transactionType,
      params,
      true
    );
    if (data != null) {
      const { physicalOrder, transactionStatus } = data;
      const { billing, lineItems } = physicalOrder;
      const { payments } = billing!;
      setPhysicalOrder(physicalOrder);
      setPayments(payments!);
      setLineItems(lineItems!);
      setBillingData(billing!);
      // setTransactionInfo((prev) => ({ ...prev, status: transactionStatus! }));
      setTransactionInfo((prev) => ({ ...prev, status: TRANSACTION_STATUS.PAYMENT_INITIATED! }));

    }
    return respond(data, error);
  };
};

export const cancelTransaction: TCancelTransactionService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { error, data } = await POST<TCancelTransactionData>(
      "/cancelTransaction?type=" + transactionType,
      { ...params },
      true
    );
    return respond(data, error);
  };
};


export const fetchTransactionDocuments: TFetchTransactionDocumentsService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const { error, data } = await POST<TFetchTransactionDocumentsData>(
      "/fetchTransactionDocuments?type=" + transactionType,
      { ...params },
      true
    );
    return respond(data, error);
  };
};

export const fetchEcomTransactionDocuments: TFetchTransactionDocumentsService = ({
  transactionType,
  ...params
}) => {
  return async () => {
    const { error, data } = await POST<TFetchTransactionDocumentsData>(
      "/fetchEcomRedeemAtPvcTransactionDocuments?type=" + transactionType,
      { ...params },
      true
    );
    return respond(data, error);
  };
};


export const fetchTransactionDocumentsList: TFetchTransactionDocumentsListService = ({
  transactionType,
  ...params
}) => {
  return async (store: TStore) => {
    const {setTransactionDocuments} = store.transactionsStore
    const { error, data } = await POST<TFetchTransactionDocumentsListData>(
      "/fetchTransactionDocumentsList?type=" + transactionType,
      { ...params },
      true
    );
    if(data) setTransactionDocuments(data.documentsList)
    return respond(data, error);
  };
};


export const deleteBillingPayment: TDeleteBillingPaymentService = (params) => {
  return async (store: TStore) => {
    const { transactionType } = params;
    const { setPayments, setBillingData, setTransactionInfo, transactionInfo } =
      store.transactionsStore;
    const { setCustomerConfig } = store.customerStore
    const { error, data } = await POST<TDeleteBillingPaymentData>(
      "/deleteBillingPayment?type=" + transactionType,
      params,
      true
    );
    if (data !== null) {
      if (data.updatedWalletBalance) setCustomerConfig(prev => ({ ...prev, walletBalance: data.updatedWalletBalance }))
      setBillingData(data.billing as TBilling);
      setPayments((prev) => prev.filter(payment => payment.id != params.paymentId));
      setTransactionInfo({
        ...transactionInfo,
        status: TRANSACTION_STATUS.PAYMENT_INITIATED,
      });
    }
    return respond(data, error);
  };
};

export const confirmBillingPayments: TConfirmBillingPaymentService = (params) => {
  return async (store: TStore) => {
    const { transactionType } = params;
    const { setTransactionInfo, transactionInfo } =
      store.transactionsStore;
    const { error, data } = await POST<TConfirmBillingPaymentData>(
      "/confirmBillingPayments?type=" + transactionType,
      params,
      true
    );
    if (data !== null) {
      setTransactionInfo({
        ...transactionInfo,
        status: data.status,
      });
    }
    return respond(data, error);
  }
}

export const fetchConsentForm: TFetchConcentDocumentsService = ({ transactionType, ...params }) => {
  return async (store: TStore) => {
    const { error, data } = await POST<TFetchConsentData>(
      "/fetchConsentForm?type=" + transactionType,
      { ...params },
      true
    );

    return respond(data, error);
  };
};
export const fetchAnnexure: TFetchAnnexureDocumentsService = ({ transactionType, ...params }) => {
  return async (store: TStore) => {
    const { error, data } = await POST<TFetchConsentData>(
      "/fetchAnnexure?type=" + transactionType,
      { ...params },
      true
    );

    return respond(data, error);
  };
};

export const getTransactionImages: TGetTransactionImages = ({ ...params }) => {
  return async (store: TStore) => {
    const { setTransactionImages } = store.transactionsStore;
    const { error, data } = await POST<TGetTransactionImagesData[]>(
      "/getTransactionImages",
      { ...params },
      true
    );

    if(data !== null){
      setTransactionImages(data);
    }

    return respond(data, error);
  }
}

export const createTransactionImage: TCreateTransactionImage = ({ transactionType, ...params }) => {
  return async (store: TStore) => {
    const { transactionImages, setTransactionImages } = store.transactionsStore;
    const { error, data } = await POST<TCreateTransactionImageData>(
      "/createTransactionImage?type=" + transactionType,
      { ...params },
      true
    );

    if(data !== null){
      setTransactionImages([
        ...transactionImages,
        data
      ]);
    }

    return respond(data, error);
  }
}

export const deleteTransactionImage: TDeleteTransactionImage = ({ transactionType, ...params }) => {
  return async (store: TStore) => {
    const { transactionImages, setTransactionImages } = store.transactionsStore;
    const { error, data } = await POST<TDeleteTransactionImageData>(
      "/deleteTransactionImage?type=" + transactionType,
      { ...params },
      true
    );

    if(data !== null){
      const updatedTransactionImagesList = transactionImages.filter(elem => elem.id !== data.id);
      setTransactionImages(updatedTransactionImagesList);
    }

    return respond(data, error);
  }
}

export const createProductScanException: TCreateProductScanException = params => {
  return async () => {
    const { error, data } = await POST<TCreateProductScanExceptionData>(
      "/createProductScanException",
      { ...params },
      true
    );

    if (error === null) return respond({ success: true }, null);
    else return respond(null, error);
  }
}

export const fetchXRFReport: TFetchXRFReportService = ({ transactionType, ...params }) => {
  return async (store: TStore) => {
    const { error, data } = await POST<TFetchXRFData>(
      "/fetchXRFReport?type=" + transactionType,
      { ...params },
      true
    );

    return respond(data, error);
  };
};

export const getUserAuthLetter: any = ({ ...params }) => {
  return async (store: TStore) => {
    const { setUserAuthFiles } = store.transactionsStore;
    const { error, data } = await POST<any[]>(
      "/getUserAuthLetter",
      { ...params },
      true
    );

    if(data !== null){
      setUserAuthFiles(data);
    }

    return respond(data, error);
  }
}

export const createUserAuthLetter = ({ transactionType, ...params }: any) => {
  return async (store: TStore) => {
    const { userAuthFiles, setUserAuthFiles } = store.transactionsStore;
    const { error, data } = await POST<any>(
      "/createUserAuthLetter?type=" + transactionType,
      { ...params },
      true
    );

    if(data !== null){
      setUserAuthFiles([
        ...userAuthFiles,
        data
      ]);
    }

    return respond(data, error);
  }
}

export const deleteUserAuthLetter = ({ transactionType, ...params }: any) => {
  return async (store: TStore) => {
    const { userAuthFiles, setUserAuthFiles } = store.transactionsStore;
    const { error, data } = await POST<any>(
      "/deleteUserAuthLetter?type=" + transactionType,
      { ...params },
      true
    );

    if(data !== null){
      const updatedUserAuthFiles = userAuthFiles.filter(elem => elem.id !== data.id);
      setUserAuthFiles(updatedUserAuthFiles);
    }

    return respond(data, error);
  }
}

export const fetchDebitNote = ({ transactionType, ...params }: any ) => {
  return async (store: TStore) => {
    const { error, data } = await POST<any>(
      "/fetchDebitNote?type=" + transactionType,
      { ...params },
      true
    );

    return respond(data, error);
  };
};

export const fetchBankDetails = ({ transactionType, ...params }: any) => {
  return async (store: TStore) => {
    const { error, data } = await POST<any>(
      "/fetchBankDetails?type=" + transactionType,
      { ...params },
      true
    );

    return respond(data, error);
  };
};

export const fetchHSNCodeDetails = ({ transactionType, ...params }: any) => {
  return async (store: TStore) => {
    const { error, data } = await POST<any>(
      "/fetchHSNCodeDetails?type=" + transactionType,
      { ...params },
      true
    );

    return respond(data, error);
  };
};

export const getTransactionInvoice: TGetTransactionInvoices = ({ ...params }) => {
  return async (store: TStore) => {
    const { setTransactionInvoices } = store.transactionsStore;
    const { error, data } = await POST<TGetTransactionInvoicesData[]>(
      "/getTransactionInvoice",
      { ...params },
      true
    );

    if(data !== null){
      setTransactionInvoices(data);
    }

    return respond(data, error);
  }
}

export const createTransactionInvoice: TCreateTransactionInvoice = ({ transactionType, ...params }) => {
  return async (store: TStore) => {
    const { transactionInvoices, setTransactionInvoices } = store.transactionsStore;
    const { error, data } = await POST<TCreateTransactionInvoiceData>(
      "/createTransactionInvoice?type=" + transactionType,
      { ...params },
      true
    );

    if(data !== null){
      setTransactionInvoices([
        ...transactionInvoices,
        data
      ]);
    }

    return respond(data, error);
  }
}

export const deleteTransactionInvoice: TDeleteTransactionInvoice = ({ transactionType, ...params }) => {
  return async (store: TStore) => {
    const { transactionInvoices, setTransactionInvoices } = store.transactionsStore;
    const { error, data } = await POST<TDeleteTransactionInvoiceData>(
      "/deleteTransactionInvoice?type=" + transactionType,
      { ...params },
      true
    );

    if(data !== null){
      const updatedTransactionImagesList = transactionInvoices.filter(elem => elem.id !== data.id);
      setTransactionInvoices(updatedTransactionImagesList);
    }

    return respond(data, error);
  }
}